import { Injectable } from "@angular/core";
import { PatientView } from "../../../model/patient/patient-view";
import { PatientService } from "../../patient.service";
import { MedicalOrderOperation } from "../../../model/medical-order-operation";
import { SafeResourceUrl } from "@angular/platform-browser";
import { PdfService } from "../../pdf.service";
import { ReportService } from "../../report.service";
import { NotificationToastService } from "../../notification-toast.service";
import {
  StructuredDiagnosisColumnItem,
  StudykeyImage,
  StudyReportEditInfo,
} from "../../../model/study-report-text";
import { StudyReportEdit } from "../../../model/study-report-edit";
import { StudyReportTemplateFilter } from "../../../model/study-report-template-filter";
import { ReferringPhysicianView } from "../../../model/referring-physician";
import { PhysicianService } from "../../physician.service";
import { ReportViewer } from "../../../model/report-viewer";
import { StudyReportService } from "../../study-report/study-report.service";
import { StudyReportTypeModalityEnum } from "src/app/model/study-report/study-report-type-modality";

export interface IStudyReportWizard {
  patientId: number;
  medicalOrderId: number;
  studyReportId: number;
  deliveryDate: Date;
  practiceName: string;
  serviceName: string;
  serviceInformaTxControl: boolean;
  medicalAppointmentId: number;
  physicianId: number;
  isReadOnly: boolean;
  editInWord: boolean;
  setControlAdmin: boolean;

  canReverse(): boolean;
  getStudyReportPdfSafeResourceUrl(): SafeResourceUrl;
  getMedicalOrderOperation(): MedicalOrderOperation;
  getStudyReportViewer(studyReportEdit: StudyReportEdit): Promise<ReportViewer>;
  refreshStudyReportPdf(
    studyReportEdit: StudyReportEdit
  ): Promise<SafeResourceUrl>;
  studyReportUpdate(studyReportEdit: StudyReportEdit): Promise<boolean>;
  studyReportConfirm(
    typeModalityEnum: StudyReportTypeModalityEnum,
    studyReportEdit: StudyReportEdit,
    structuredDiagnosisColumnItems: StructuredDiagnosisColumnItem[]
  ): Promise<boolean>;
  studyReportStateReverse(callBack: any): void;
}

@Injectable()
export class StudyReportWizardService {
  studyReportWizard: IStudyReportWizard;
  patient: PatientView;
  referringPhysicianView: ReferringPhysicianView;
  studyReportPath: SafeResourceUrl;
  studyReportEditInfo: StudyReportEditInfo;
  studyReportTemplateFilters: StudyReportTemplateFilter[];
  studykeyImage: StudykeyImage;

  constructor(
    private patientService: PatientService,
    private studyReportService: StudyReportService,
    private physicianService: PhysicianService,
    private notificationToastService: NotificationToastService
  ) {}

  initialize(studyReportWizard: IStudyReportWizard) {
    this.studyReportWizard = null;
    this.patient = null;
    this.referringPhysicianView = null;
    this.studyReportPath = null;
    this.studyReportEditInfo = null;
    this.studyReportTemplateFilters = null;
    this.clearImagesKeys();
    this.studyReportWizard = studyReportWizard;


  }

  get editInWord(): boolean {
    return this.studyReportWizard.editInWord;
  }
  get isReadOnly(): boolean {
    return this.studyReportWizard.isReadOnly;
  }
  get medicalAppointmentId(): number {
    return this.studyReportWizard.medicalAppointmentId;
  }
  get practiceName(): string {
    return this.studyReportWizard.practiceName;
  }
  get serviceName(): string {
    return this.studyReportWizard.serviceName;
  }

  get serviceInformaTxControl(): boolean {
    return this.studyReportWizard.serviceInformaTxControl;
  }


  get deliveryDate(): Date {
    return this.studyReportWizard.deliveryDate;
  }
  get studyReportId(): number {
    return this.studyReportWizard.studyReportId;
  }
  get physicianId(): number {
    return this.studyReportWizard.physicianId;
  }
  get medicalOrderId(): number {
    return this.studyReportWizard.medicalOrderId;
  }
  get patientId(): number {
    return this.studyReportWizard.patientId;
  }

  get setControlAdmin(): boolean {
    return this.studyReportWizard.setControlAdmin;
  }

  clearImagesKeys() {
    this.studykeyImage = new StudykeyImage();
    this.studykeyImage.KeyImageIds = [];
    this.studykeyImage.SelectedKeyImageIds = [];
  }

  getMedicalOrderOperation(): MedicalOrderOperation {
    return this.studyReportWizard.getMedicalOrderOperation();
  }

  async getReferringPhysicianView(): Promise<ReferringPhysicianView> {
    if (this.referringPhysicianView) {
      return this.referringPhysicianView;
    } else {
      return await this.physicianService.getReferringPhysicianViewByMedicalAppointmentId(
        this.studyReportWizard.medicalAppointmentId
      );
    }
  }

  async getPatientView(): Promise<PatientView> {
    if (this.patient) {
      return this.patient;
    } else {
      return await this.patientService.getPatientView(
        this.studyReportWizard.patientId
      );
    }
  }

  async getStudyReportViewer(
    studyReportEdit: StudyReportEdit
  ): Promise<ReportViewer> {
    try {
      return await this.studyReportWizard.getStudyReportViewer(studyReportEdit);
    } catch (err) {
      this.notificationToastService.error(JSON.stringify(err.error), "Informe");
    }
  }

  async refreshStudyReportPdf(studyReportEdit: StudyReportEdit) {
    try {
      await this.studyReportWizard.refreshStudyReportPdf(studyReportEdit);
    } catch (err) {
      this.notificationToastService.error(JSON.stringify(err.error), "Informe");
    }
  }

  async studyReportConfirm(
    typeModalityEnum: StudyReportTypeModalityEnum,
    studyReportEdit: StudyReportEdit,
    structuredDiagnosisColumnItems: StructuredDiagnosisColumnItem[]
  ): Promise<boolean> {
    try {
      return await this.studyReportWizard.studyReportConfirm(
        typeModalityEnum,
        studyReportEdit,
        structuredDiagnosisColumnItems
      );
    } catch (err) {
      this.notificationToastService.error(JSON.stringify(err.error), "Informe");
    }
  }

  async getStudyReportEditInfo(): Promise<StudyReportEditInfo> {
    if (this.studyReportEditInfo) {
      return this.studyReportEditInfo;
    } else {
      this.studyReportEditInfo =
        await this.studyReportService.getStudyReportEditInfo(
          this.studyReportId
        );
      return this.studyReportEditInfo;
    }
  }

  // async getStudyReportTemplateFilters(): Promise<StudyReportTemplateFilter[]> {
  //   if (this.studyReportTemplateFilters) {
  //     return this.studyReportTemplateFilters;
  //   } else {
  //     return await this.studyReportService.getStudyReportTemplateFilters(
  //       this.physicianId,
  //       this.studyReportId
  //     );
  //   }
  // }

  async getStudyReportTemplatesText(
    studyReportTemplateIds: number[]
  ): Promise<string[]> {
    return await this.studyReportService.getStudyReportTemplatesText(
      studyReportTemplateIds
    );
  }

  async studyReportUpdate(studyReportEdit: StudyReportEdit): Promise<boolean> {
    return this.studyReportWizard.studyReportUpdate(studyReportEdit);
  }

  getStudyReportPdfSafeResourceUrl(): SafeResourceUrl {
    return this.studyReportWizard.getStudyReportPdfSafeResourceUrl();
  }

  canReverse(): boolean {
    return this.studyReportWizard.canReverse();
  }

  studyReportStateReverse(callBack: any): void {
    this.studyReportWizard.studyReportStateReverse(callBack);
  }
}
